import React, { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { devtools } from "zustand/middleware"
import { uuidv4 } from "@/lib/generate-uuidv4"
import { ConnectionStatus, TranscriptEntry } from "@/modules/speech-to-text/types"
import { Language } from "@/types/__generated__/union-types"

interface PhoneCallTranscriptionState {
  isRecording: boolean
  connectionStatus: ConnectionStatus
  currentAppointmentId: string | null
  currentProgressNoteId: string | null
  patientName: string | null
  transcripts: TranscriptEntry[]
  selectedMicrophoneId: string | null
  language: Language | null
}

interface PhoneCallTranscriptionActions {
  startRecording: (
    appointmentId: string,
    progressNoteId: string | null,
    patientName: string,
    language: Language,
  ) => void
  stopRecording: () => void
  addTranscript: (entry: Omit<TranscriptEntry, "id">) => void
  clearTranscripts: () => void
  setConnectionStatus: (status: ConnectionStatus) => void
  setSelectedMicrophoneId: (microphoneId: string | null) => void
  reset: () => void
}

type PhoneCallTranscriptionStore = PhoneCallTranscriptionState & PhoneCallTranscriptionActions

const initialState: PhoneCallTranscriptionState = {
  isRecording: false,
  connectionStatus: "disconnected",
  currentAppointmentId: null,
  currentProgressNoteId: null,
  patientName: null,
  transcripts: [],
  selectedMicrophoneId: null,
  language: null,
}

const createPhoneCallTranscriptionStore = () => {
  return create<PhoneCallTranscriptionStore>()(
    devtools(
      (set) => ({
        ...initialState,
        startRecording: (appointmentId, progressNoteId, patientName, language) => {
          set({
            isRecording: true,
            currentAppointmentId: appointmentId,
            currentProgressNoteId: progressNoteId,
            patientName,
            language,
          })
        },
        stopRecording: () => {
          set({
            isRecording: false,
            connectionStatus: "disconnected",
          })
        },
        addTranscript: (entry) => {
          const newEntry = {
            ...entry,
            id: `phone-${entry.timestamp}-${uuidv4()}`,
          }

          set((state) => ({
            transcripts: [...state.transcripts, newEntry],
          }))
        },
        clearTranscripts: () => {
          set({ transcripts: [] })
        },
        setConnectionStatus: (connectionStatus) => {
          set({ connectionStatus })
        },
        setSelectedMicrophoneId: (microphoneId) => {
          set({ selectedMicrophoneId: microphoneId })
        },
        reset: () => {
          set(initialState)
        },
      }),
      { name: "phoneCallTranscription" },
    ),
  )
}

type PhoneCallTranscriptionStoreType = ReturnType<typeof createPhoneCallTranscriptionStore>

const PhoneCallTranscriptionStoreContext = createContext<PhoneCallTranscriptionStoreType | null>(null)

type PhoneCallTranscriptionStoreProviderProps = React.PropsWithChildren

export const PhoneCallTranscriptionStoreProvider = ({ children }: PhoneCallTranscriptionStoreProviderProps) => {
  const storeRef = useRef<PhoneCallTranscriptionStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createPhoneCallTranscriptionStore()
  }
  return (
    <PhoneCallTranscriptionStoreContext.Provider value={storeRef.current}>
      {children}
    </PhoneCallTranscriptionStoreContext.Provider>
  )
}

export function usePhoneCallTranscriptionStore<T>(selector: (state: PhoneCallTranscriptionStore) => T): T {
  const store = useContext(PhoneCallTranscriptionStoreContext)
  if (!store) throw new Error("Missing PhoneCallTranscriptionStoreProvider in the tree")
  return useStore(store, selector)
}

export function usePhoneCallTranscriptionStoreApi() {
  const store = useContext(PhoneCallTranscriptionStoreContext)
  if (!store) throw new Error("Missing PhoneCallTranscriptionStoreProvider in the tree")
  return store
}
